import { asRecord, unwrapSnbt, unwrapString } from './unwrap';

const SKIP_ICON_IDS = new Set([
  'item',
  'item_stack',
  'ftbquests:custom',
  'ftbquests:item',
  'ftblibrary:icon',
  'ftblibrary:custom_icon',
]);

/**
 * Normalize an FTB / Minecraft item id into `namespace:path`.
 * Accepts `diamond`, `minecraft:diamond`, `item:minecraft:diamond`.
 */
export function normalizeItemId(raw: string): string | null {
  let s = raw.trim();
  if (!s) return null;
  if (s.startsWith('item:')) s = s.slice(5);
  if ((s.startsWith('{') || s.includes('id:')) && s.includes(':')) {
    const m = s.match(/id:\s*"?([a-z0-9_.]+:[a-z0-9_./-]+)"?/i);
    if (m) s = m[1];
  }
  s = s.replace(/^["']+|["']+$/g, '');
  if (!s.includes(':')) s = `minecraft:${s}`;
  const parsed = s.split(':');
  if (parsed.length < 2 || !parsed[0] || !parsed[1]) return null;
  if (SKIP_ICON_IDS.has(s)) return null;
  return s;
}

/**
 * Pull a namespaced item id out of the many shapes FTB Quests uses for
 * `icon`, task `item`, and reward `item` fields.
 */
export function extractItemId(value: unknown): string | null {
  const v = unwrapSnbt(value);
  if (v == null) return null;
  if (typeof v === 'string') return normalizeItemId(v);
  if (Array.isArray(v)) {
    for (const entry of v) {
      const id = extractItemId(entry);
      if (id) return id;
    }
    return null;
  }

  const obj = asRecord(v);
  if (!obj) return null;

  const nestedKeys = ['item', 'stack', 'Item', 'id'];
  for (const key of nestedKeys) {
    if (!(key in obj)) continue;
    const nested = obj[key];
    if (key === 'id') {
      const id = unwrapString(nested);
      if (id && id.includes(':') && !SKIP_ICON_IDS.has(id) && !id.startsWith('ftb')) {
        return normalizeItemId(id);
      }
      continue;
    }
    const fromNested = extractItemId(nested);
    if (fromNested) return fromNested;
  }

  return null;
}

/**
 * First namespaced item id in `value`, including SNBT lists / item arrays
 * (`item: ["minecraft:clock", "minecraft:map"]`).
 */
export function firstItemId(value: unknown): string | null {
  return extractItemId(value);
}

export function collectItemIdsFromUnknown(value: unknown, into: Set<string>): void {
  const v = unwrapSnbt(value);
  if (v == null) return;
  if (typeof v === 'string') {
    const id = normalizeItemId(v);
    if (id) into.add(id);
    return;
  }
  if (Array.isArray(v)) {
    for (const entry of v) collectItemIdsFromUnknown(entry, into);
    return;
  }
  const obj = asRecord(v);
  if (!obj) return;
  const extracted = extractItemId(obj);
  if (extracted) into.add(extracted);
  for (const child of Object.values(obj)) {
    if (child && typeof child === 'object') collectItemIdsFromUnknown(child, into);
  }
}
